import { useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';
import { useRect } from 'react-use-rect';
import type { BlockProps } from '../../lib/card';
import { CardLayout } from '../../lib/card';
import {
  FollowButton,
  SOURCE_ICON_SIZE,
  SourceLink,
  User
} from '../components';
import { usePost } from '../Post';
import { usePostSettings } from '../utils';

interface CardUserBlockProps extends BlockProps {
  layout?: 'feed' | 'classic';
  sourceAlign?: CSSProperties['alignSelf'];
  withSource?: boolean;
  centered?: boolean;
}

export function CardUserBlock({
  sourceAlign,
  layout,
  withSource = false,
  centered = false,
  ...forwardingProps
}: CardUserBlockProps) {
  const { user, source, timestamp, url } = usePost();
  const {
    textColor,
    showFollowButton: withFollowButton = false,
    source: sourceSettings,
    user: {
      showName: userShowName,
      showPicture: userShowPicture,
      showDate: userShowDate,
      showBadge: userShowBadge = false,
      show: userShow
    }
  } = usePostSettings();

  const [width, setWidth] = useState(0);
  const [measureRef] = useRect(
    ({ width }) => requestAnimationFrame(() => setWidth(width)),
    {
      resize: true
    }
  );

  if (!userShow && !sourceSettings.show && !withFollowButton) {
    return null;
  }

  const finalShowSource = withSource && sourceSettings.show && width >= 150;
  const showAvatar =
    source.type === 'rss'
      ? userShowPicture && width > 150 && !!user?.image
      : userShowPicture && width > 150;

  return (
    <UserBlock
      {...forwardingProps}
      ref={measureRef}
      _between={!centered}
      _noShrink
    >
      <UserContainer _center={centered}>
        {userShow && (
          <StyledUser
            user={user}
            timestamp={timestamp || 0}
            color={textColor}
            center={centered}
            showName={userShowName}
            showBadge={userShowBadge}
            showDate={userShowDate}
            showPicture={showAvatar}
            _withRightOffset={finalShowSource || withFollowButton}
          />
        )}

        {(finalShowSource || withFollowButton) && (
          <SourceContainer _gap={layout === 'classic' ? 6 : 12}>
            {withFollowButton && <FollowButton />}

            {finalShowSource && (
              <SourceLink
                {...sourceSettings}
                url={url}
                sourceAlign={sourceAlign}
              />
            )}
          </SourceContainer>
        )}
      </UserContainer>
    </UserBlock>
  );
}

const UserContainer = styled.div<{
  _center: boolean;
}>`
  display: inline-flex;
  justify-content: ${({ _center }) => (_center ? 'center' : 'space-between')};
  align-items: center;
  width: 100%;
`;

const SourceContainer = styled.div<{
  _gap: number;
}>`
  display: inline-flex;
  ${({ _gap }) => !!_gap && `gap: ${_gap}px`};
`;

const UserBlock = styled(CardLayout.Block)``;

const StyledUser = styled(User)<{
  _withRightOffset: boolean;
}>`
  min-width: 0;

  ${({ _withRightOffset }) =>
    _withRightOffset &&
    css`
      padding-right: 8px;
      max-width: calc(100% - ${SOURCE_ICON_SIZE}px);
    `}
`;
